import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { getSettings, getWidgets } from '../lib/api';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, widgetsRes] = await Promise.all([
          getSettings(),
          getWidgets()
        ]);
        setSettings(settingsRes.data);
        setWidgets(widgetsRes.data);
      } catch (error) {
        console.error('Error fetching navbar data:', error);
      }
    };
    fetchData();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const openMenu = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/packages', label: 'Packages' },
    { path: '/media', label: 'Photos & Media' },
    { path: '/faqs', label: 'FAQs' },
    { path: '/reviews', label: 'Reviews' },
    { path: '/about', label: 'About' },
  ];

  // Widget page links - always show these in nav
  const widgetLinks = [
    { path: '/contact', label: 'Contact' },
    { path: '/availability', label: 'Check Availability' },
    { path: '/client-login', label: 'Client Login' },
  ];

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-neutral-100/50" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0" data-testid="nav-logo">
              {settings?.logo_data ? (
                <img src={settings.logo_data} alt={settings?.site_name || 'DJ'} className="h-8 sm:h-12 w-auto" />
              ) : (
                <span className="text-lg sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {settings?.site_name || 'DJ Services'}
                </span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm xl:text-base ${isActive(link.path) ? 'nav-link-active' : ''}`}
                  data-testid={`nav-${link.path.slice(1)}`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Widget Page Links */}
              {widgetLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-sm xl:text-base ${isActive(link.path) ? 'nav-link-active' : ''}`}
                  data-testid={`nav-${link.path.slice(1)}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              className="lg:hidden relative flex items-center justify-center w-14 h-14 -mr-3 touch-manipulation"
              onClick={openMenu}
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openMenu();
              }}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              aria-expanded={isOpen}
              style={{ 
                WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                cursor: 'pointer',
                zIndex: 100
              }}
            >
              <Menu size={28} strokeWidth={2} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-white lg:hidden overflow-y-auto"
          style={{ zIndex: 60 }}
          data-testid="mobile-menu"
        >
          {/* Mobile Menu Header */}
          <div className="sticky top-0 bg-white flex items-center justify-between h-16 px-4 border-b border-neutral-100">
            <Link to="/" className="flex items-center gap-2" onClick={closeMenu}>
              {settings?.logo_data ? (
                <img src={settings.logo_data} alt={settings?.site_name || 'DJ'} className="h-8 w-auto" />
              ) : (
                <span className="text-lg font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {settings?.site_name || 'DJ Services'}
                </span>
              )}
            </Link>
            <button
              type="button"
              className="flex items-center justify-center w-14 h-14 -mr-3 touch-manipulation"
              onClick={closeMenu}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeMenu();
              }}
              aria-label="Close menu"
              style={{ 
                WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}
            >
              <X size={28} strokeWidth={2} />
            </button>
          </div>
          
          {/* Mobile Menu Links */}
          <div className="py-4 px-4">
            <div className="space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-4 px-4 text-lg font-medium rounded-lg transition-colors touch-manipulation ${
                    isActive(link.path) 
                      ? 'bg-neutral-900 text-white' 
                      : 'text-neutral-700 active:bg-neutral-100'
                  }`}
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Divider */}
              <div className="border-t border-neutral-200 my-4"></div>
              
              {/* Widget Page Links in Mobile */}
              {widgetLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-4 px-4 text-lg font-medium rounded-lg transition-colors touch-manipulation ${
                    isActive(link.path) 
                      ? 'bg-neutral-900 text-white' 
                      : 'text-neutral-700 active:bg-neutral-100'
                  }`}
                  onClick={closeMenu}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
